import PropTypes from "prop-types";
import { Cpu, RefreshCw, AlertTriangle, Clock, CheckCircle2, XCircle, Archive } from "lucide-react";
import StatusBadge from "./StatusBadge.jsx";

/**
 * DigitalProductStatus — estado y precio del producto digital (MVP-04A).
 *
 * Presentacional y reutilizable por propietario y administración. Muestra el
 * estado de la versión, el «precio calculado» y el «precio final revisado» como
 * conceptos distintos, y siempre con lenguaje de estimación: el paquete aún
 * requiere revisión y NUNCA es comprable en 04A. No incluye CTA de compra ni de
 * descarga.
 *
 * Estados: empty | loading | calculando | invalido | pendiente_revision |
 * aprobado | rechazado | obsoleto | error.
 *
 * Todos los importes y el estado los fija el backend; aquí solo se presentan.
 */

const STATE_META = {
    calculando: { tone: "info", icon: Clock, label: "Calculando" },
    pendiente_revision: { tone: "warning", icon: Clock, label: "Pendiente de revisión" },
    aprobado: { tone: "success", icon: CheckCircle2, label: "Aprobado" },
    rechazado: { tone: "error", icon: XCircle, label: "Rechazado" },
    obsoleto: { tone: "neutral", icon: Archive, label: "Obsoleto" },
    invalido: { tone: "error", icon: AlertTriangle, label: "No válido" },
};

const defaultFmt = (v, c = "EUR") =>
    v == null ? null : new Intl.NumberFormat("es-ES", { style: "currency", currency: c }).format(Number(v) || 0);

// Volumen comprensible (cm³, no mm³ interno) con precisión de lectura, no de
// cálculo — el backend guarda mucha más precisión de la que hace falta mostrar.
const formatVolumeCm3 = (v) =>
    new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(Number(v) || 0);

export default function DigitalProductStatus({
    state = "empty",
    calculatedPrice = null,
    finalPrice = null,
    currency = "EUR",
    overrideReason = "",
    rejectionReason = "",
    backgroundCount = null,
    volumeCm3 = null,
    priceBreakdown = null,
    busy = false,
    onCalculate = null,
    onRetry = null,
    formatCurrency = defaultFmt,
    className = "",
}) {
    const btn = (label, handler, tone = "neutral") =>
        handler ? (
            <button
                type="button"
                onClick={handler}
                disabled={busy}
                aria-busy={busy}
                className={
                    "mt-2 inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium " +
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 " +
                    "focus-visible:ring-offset-base-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed " +
                    (tone === "error"
                        ? "border-error/50 text-error hover:bg-error/10"
                        : "border-base-content/30 text-base-content hover:bg-base-content/10")
                }
            >
                <RefreshCw className={`w-4 h-4 ${busy ? "motion-safe:animate-spin" : ""}`} aria-hidden /> {label}
            </button>
        ) : null;

    const Header = () => (
        <div className="flex items-center gap-2 text-sm font-semibold text-base-content">
            <Cpu className="w-4 h-4 shrink-0 text-base-content/70" aria-hidden /> Producto digital
        </div>
    );

    // --- Sin cálculo todavía ---
    if (state === "empty") {
        return (
            <div className={`rounded-xl border border-base-content/15 p-4 ${className}`}>
                <Header />
                <p className="mt-1 text-xs text-base-content/60">
                    Aún no has calculado el precio digital de este diseño. El precio es una
                    estimación y el paquete requiere revisión antes de estar disponible.
                </p>
                {btn(busy ? "Calculando…" : "Calcular precio (estimado)", onCalculate)}
            </div>
        );
    }

    // --- Cargando / calculando ---
    if (state === "loading" || state === "calculando") {
        return (
            <div className={`rounded-xl border border-base-content/15 p-4 ${className}`} aria-live="polite">
                <Header />
                <div className="mt-2 h-4 w-40 rounded bg-base-content/15 motion-safe:animate-pulse"
                     aria-label="Calculando el precio digital" />
                <p className="mt-2 text-xs text-base-content/50">Midiendo la geometría y calculando…</p>
            </div>
        );
    }

    // --- Error de red ---
    if (state === "error") {
        return (
            <div role="alert" className={`rounded-xl border border-error/40 bg-error/10 p-4 ${className}`}>
                <Header />
                <p className="mt-1 text-xs text-base-content/70">
                    No se pudo calcular el precio digital ahora mismo.
                </p>
                {btn("Reintentar", onRetry, "error")}
            </div>
        );
    }

    const meta = STATE_META[state] || STATE_META.invalido;
    const Icon = meta.icon;
    const isApproved = state === "aprobado";
    const showBothPrices = finalPrice != null && calculatedPrice != null
        && String(finalPrice) !== String(calculatedPrice);

    return (
        <div className={`rounded-xl border border-base-content/15 p-4 ${className}`}>
            <div className="flex items-center justify-between gap-2">
                <Header />
                <StatusBadge label={meta.label} tone={meta.tone} size="sm" outline
                             icon={<Icon className="w-3.5 h-3.5" aria-hidden />} />
            </div>

            {state === "invalido" && (
                <p className="mt-2 text-xs text-error">
                    La geometría no produce una medición válida; revisa el diseño y vuelve a calcular.
                </p>
            )}
            {state === "rechazado" && (
                <p className="mt-2 text-xs text-base-content/70">
                    Revisión rechazada{rejectionReason ? `: ${rejectionReason}` : "."}
                </p>
            )}
            {state === "obsoleto" && (
                <p className="mt-2 text-xs text-base-content/60">
                    Esta versión quedó obsoleta; existe o existirá una versión sucesora.
                </p>
            )}

            {(state === "pendiente_revision" || isApproved) && (
                <dl className="mt-3 space-y-1 text-sm">
                    {backgroundCount != null && (
                        <div className="flex justify-between">
                            <dt className="text-base-content/60">Piezas de fondo</dt>
                            <dd className="font-medium">{backgroundCount}</dd>
                        </div>
                    )}
                    {volumeCm3 != null && (
                        <div className="flex justify-between">
                            <dt className="text-base-content/60">Volumen sólido</dt>
                            <dd className="font-medium">{formatVolumeCm3(volumeCm3)} cm³</dd>
                        </div>
                    )}
                    {priceBreakdown?.base != null && (
                        <div className="flex justify-between">
                            <dt className="text-base-content/60">Base ({priceBreakdown.product_type || "producto"})</dt>
                            <dd className="font-medium">{formatCurrency(priceBreakdown.base, currency)}</dd>
                        </div>
                    )}
                    {priceBreakdown?.extra_backgrounds > 0 && (
                        <div className="flex justify-between">
                            <dt className="text-base-content/60">
                                Fondos adicionales ({priceBreakdown.extra_backgrounds})
                            </dt>
                            <dd className="font-medium">{formatCurrency(priceBreakdown.backgrounds_cost, currency)}</dd>
                        </div>
                    )}
                    {priceBreakdown?.volume_cost != null && (
                        <div className="flex justify-between">
                            <dt className="text-base-content/60">Coste por volumen</dt>
                            <dd className="font-medium">{formatCurrency(priceBreakdown.volume_cost, currency)}</dd>
                        </div>
                    )}
                    <div className="flex justify-between border-t border-base-content/10 pt-1">
                        <dt className="text-base-content/60">Precio calculado</dt>
                        <dd className="font-medium">{formatCurrency(calculatedPrice, currency) || "—"}</dd>
                    </div>
                    {showBothPrices && (
                        <div className="flex justify-between">
                            <dt className="text-base-content/60">Precio final revisado</dt>
                            <dd className="font-semibold">{formatCurrency(finalPrice, currency)}</dd>
                        </div>
                    )}
                </dl>
            )}

            {overrideReason && (
                <p className="mt-2 text-[11px] text-base-content/50">
                    Precio revisado por administración: {overrideReason}
                </p>
            )}

            {/* En 04A el paquete no es comprable ni descargable: se dice claramente. */}
            <p className="mt-3 text-[11px] leading-relaxed text-base-content/50">
                {isApproved
                    ? "Aprobado para preparación. El paquete descargable aún no está disponible; no promete impresión inmediata ni compatibilidad con una impresora concreta."
                    : "Precio estimado, sujeto a revisión administrativa. Todavía no disponible para compra ni descarga."}
            </p>

            {(state === "invalido" || state === "rechazado" || state === "pendiente_revision") && onCalculate &&
                btn(busy ? "Recalculando…" : "Recalcular", onCalculate)}
        </div>
    );
}

DigitalProductStatus.propTypes = {
    state: PropTypes.oneOf([
        "empty", "loading", "error",
        "calculando", "invalido", "pendiente_revision", "aprobado", "rechazado", "obsoleto",
    ]),
    calculatedPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    finalPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
    overrideReason: PropTypes.string,
    rejectionReason: PropTypes.string,
    backgroundCount: PropTypes.number,
    volumeCm3: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    priceBreakdown: PropTypes.shape({
        product_type: PropTypes.string,
        base: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        extra_backgrounds: PropTypes.number,
        backgrounds_cost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        volume_cost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    busy: PropTypes.bool,
    onCalculate: PropTypes.func,
    onRetry: PropTypes.func,
    formatCurrency: PropTypes.func,
    className: PropTypes.string,
};
